import React, { useState, useRef } from "react";
import LargeFileUploader from "./LargeFileUploader";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import "../styles/Header.css";
import { useUploadStatusListener } from "../hooks/firestore";

const MAX_SIZE = 100 * 1024 * 1024 * 1024; // 100GB in bytes

const Header = () => {
  // UI states
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [software, setSoftware] = useState("none");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [usagePermission, setUsagePermission] = useState(false);
  const [emailPermission, setEmailPermission] = useState(false);

  // UploadPanel states
  const [uploadPanelVisible, setUploadPanelVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [aggregatedSize, setAggregatedSize] = useState(0);

  // Uploader states
  const [filesDropped, setFilesDropped] = useState([]);
  const [compressing, setCompressing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [uploadComplete, setUploadComplete] = useState(false);
  const [fileInputError, setFileInputError] = useState("");
  const [currentUploadId, setCurrentUploadId] = useState(null);

  const uploaderRef = useRef(null);

  const handleContributeClick = () => {
    if (!isPanelOpen) {
      document.querySelector(".upload-panel").scrollIntoView({
        behavior: "smooth",
      });
    }
    setIsPanelOpen(!isPanelOpen);
  };

  const onDrop = (data) => {
    const { totalSize, files, folders } = data;
    const parsedFolders = folders.map((folder) => ({
      name: folder.folderName,
      size: folder.totalSize,
    }));
    setFilesDropped((prev) => [...prev, ...files, ...parsedFolders]);
    setAggregatedSize((prev) => prev + totalSize);
  };

  const getTotalDuration = async (batchSize = 20) => {
    const files = uploaderRef.current.getFiles();
    const audioFiles = files.filter((f) => f.type.startsWith("audio/"));

    let totalDuration = 0;
    for (let i = 0; i < audioFiles.length; i += batchSize) {
      const batch = audioFiles.slice(i, i + batchSize);
      const durations = await Promise.all(
        batch.map(
          (f) =>
            new Promise((resolve) => {
              const audio = new Audio();
              audio.src = URL.createObjectURL(f);
              audio.addEventListener("loadedmetadata", () =>
                resolve(audio.duration)
              );
              audio.addEventListener("error", () => resolve(0));
            })
        )
      );
      totalDuration += durations.reduce((acc, duration) => acc + duration, 0);
    }

    return totalDuration;
  };

  const getNumberOfAudioFiles = () => {
    const files = uploaderRef.current.getFiles();
    return files.filter((f) => f.type.startsWith("audio/")).length;
  };

  const getNumberOfLabelFiles = () => {
    const files = uploaderRef.current.getFiles();
    return files.filter((f) => !f.type.startsWith("audio/")).length;
  };

  const getResumableUploadUrl = async () => {
    const totalDuration = await getTotalDuration();
    const response = await fetch(
      "https://generateuploadurl-umtiunxwoa-ey.a.run.app",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userEmail: email,
          userName: name,
          userSoftware: software,
          userEmailPermission: emailPermission,
          numAudioFiles: getNumberOfAudioFiles(),
          totalDuration: totalDuration,
          hasLabelFile: getNumberOfLabelFiles() > 0,
          contentType: "application/zip",
        }),
      }
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to get resumable upload URL");
    }
    const { uploadId, uploadUrl } = data;
    console.log(`Uploading file as ${uploadId}...`);
    setCurrentUploadId(uploadId);
    return uploadUrl;
  };

  const handleUpload = async () => {
    setUploadPanelVisible(true);
    setUploadError("");
    if (uploaderRef.current) {
      uploaderRef.current.startUpload();
    }
    console.log("Upload started");
  };

  useUploadStatusListener(currentUploadId, () => {
    uploaderRef.current && uploaderRef.current.completeUpload();
  });

  const handlePauseUpload = () => {
    if (uploaderRef.current) {
      uploaderRef.current.pauseUpload();
      setIsPaused(true);
    }
  };

  const handleResumeUpload = () => {
    if (uploaderRef.current) {
      uploaderRef.current.resumeUpload();
      setUploadError("");
      setIsPaused(false);
    }
  };

  // Calculate progress for the dropzone overlay.
  const getFileProgress = () => {
    if (filesDropped.length === 0) return 0;
    const totalSize = filesDropped.reduce((acc, { size }) => acc + size, 0);
    return (totalSize / MAX_SIZE) * 100;
  };

  return (
    <header className="header">
      <img
        src="logo.svg"
        alt="Code for Conservation Logo"
        className="header-logo"
      />
      <div className="header-container">
        <h1>
          <span className="bigbat-title-italic">The</span>
          <span className="bigbat-title">BigBAT Project</span>
        </h1>
        <h3 className="header-subtitle">
          Big Data <span className="small-font">for</span> Bat Bioacoustics
        </h3>
        <img src="bats.png" alt="Bats Illustration" className="header-bats" />
        <p className="header-description">
          We encourage researchers to contribute to <i>The BigBAT Project</i> by{" "}
          <strong>uploading field recordings</strong> of our beloved nocturnal
          mammals.
        </p>

        <button className="contribute-button" onClick={handleContributeClick}>
          <span className="contribute-text">
            Contribute <u>Now</u>
          </span>
        </button>
      </div>

      <div className={`upload-panel ${isPanelOpen ? "open" : ""}`}>
        <div className={`upload-panel-content ${isPanelOpen ? "open" : ""}`}>
          <LargeFileUploader
            ref={uploaderRef}
            maxSize={MAX_SIZE}
            onDrop={onDrop}
            getResumableUploadUrl={getResumableUploadUrl}
            className="filedrop"
            validExtensions={["wav", "txt", "csv", "zc", "xml"]}
            onUploadProgress={(progress) => {
              setUploadProgress(progress);
            }}
            onUploadComplete={() => {
              setUploadProgress(100);
              setUploadComplete(true);
              setIsPaused(true);
              console.log("Upload completed");
            }}
            onUploadError={(error) => {
              console.error("Upload error:", error);
              setIsPaused(true);
              setUploadError(error);
            }}
            onFileInputError={(error) => {
              console.error("File input error:", error);
              setFileInputError(error);
            }}
            compressBeforeUpload={true}
            onCompressionStart={() => setCompressing(true)}
            onCompressionStop={() => {
              setCompressing(false);
              setUploadProgress(0);
            }}
            onCompressionProgress={(progress) => {
              setUploadProgress(Math.floor(progress));
            }}
          >
            <div className="upload-icon-container">
              <img src="upload.svg" alt="Upload Icon" className="upload-icon" />
              <div
                className="upload-overlay"
                style={{
                  "--clip": `inset(0 ${100 - getFileProgress()}% 0 0)`,
                  maskImage: "url(upload.svg)",
                  WebkitMaskImage: "url(upload.svg)",
                }}
              />
            </div>
            <p className="dropzone-text">
              Drop your recordings and metadata <u>here</u>
              <br />
              <span className="dropzone-subtext">.wav .txt .csv .zc .xml</span>
            </p>
            {fileInputError && (
              <p className="dropzone-error">{fileInputError}</p>
            )}
            {filesDropped.length > 0 ? (
              <div className="file-container">
                {filesDropped.map(({ name, size }, index) => (
                  <div key={index} className="file-info">
                    <span>{name}</span>
                    <span>
                      {size > 100 * 1024 * 1024
                        ? (size / 1024 / 1024 / 1024).toFixed(2) + " GB"
                        : (size / 1024 / 1024).toFixed(2) + " MB"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ marginTop: 16 }} />
            )}
          </LargeFileUploader>

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              marginTop: 16,
              gap: 16,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <FormControl variant="standard" sx={{ textAlign: "left" }}>
                <InputLabel>Annotation Software</InputLabel>
                <Select
                  value={software}
                  onChange={(e) => setSoftware(e.target.value)}
                  label="Annotation Software"
                  defaultValue="none"
                >
                  <MenuItem value="none">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="anabat">Anabat</MenuItem>
                  <MenuItem value="avisoft">Avisoft</MenuItem>
                  <MenuItem value="bcid">BCID</MenuItem>
                  <MenuItem value="sonobat">Sonobat</MenuItem>
                  <MenuItem value="kaleidoscope">Kaleidoscope</MenuItem>
                  <MenuItem value="batscope">BatScope</MenuItem>
                  <MenuItem value="batexplorer">BatExplorer</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="E-Mail *"
                variant="standard"
                value={email}
                size="small"
                style={{ marginTop: 16 }}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                label="Name"
                variant="standard"
                size="small"
                value={name}
                style={{ marginTop: 16 }}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    value={usagePermission}
                    onChange={(e) => setUsagePermission(e.target.checked)}
                    sx={{
                      marginTop: -1,
                    }}
                  />
                }
                label={
                  <Typography>
                    I hereby grant permission for my uploaded data to be stored
                    and used for research and development purposes.*
                  </Typography>
                }
                sx={{ alignItems: "flex-start" }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    value={emailPermission}
                    onChange={(e) => setEmailPermission(e.target.checked)}
                    sx={{
                      marginTop: -1,
                      marginBottom: 3,
                    }}
                  />
                }
                label={
                  <Typography>
                    I consent to receive email updates about the current state
                    of the project.
                  </Typography>
                }
                sx={{ alignItems: "flex-start", marginTop: 2 }}
              />

              {filesDropped.length > 0 &&
              software &&
              email &&
              usagePermission ? (
                <button onClick={handleUpload}>Upload</button>
              ) : (
                <div className="disabled-button">Upload</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {uploadPanelVisible && (
        <div className="upload-panel-bottom">
          <h3 className="upload-panel-title">
            {compressing ? "Compressing" : "Uploading"} Files
          </h3>
          {uploadComplete ? (
            <div
              className="dropzone-success"
              style={{ marginBottom: 16, textAlign: "left" }}
            >
              Upload complete!
            </div>
          ) : (
            uploadError && (
              <div
                className="dropzone-error"
                style={{ marginBottom: 16, textAlign: "left" }}
              >
                {uploadError}
              </div>
            )
          )}
          <div className="progress-bar-container">
            <div
              className="progress-bar"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <div className="upload-controls">
            <span className="upload-gb">
              {(
                (uploadProgress / 100) *
                (aggregatedSize / 1024 / 1024 / 1024)
              ).toFixed(2) +
                " GB / " +
                (aggregatedSize / 1024 / 1024 / 1024).toFixed(2) +
                " GB"}
            </span>
            {!uploadComplete &&
              (isPaused ? (
                <button onClick={handleResumeUpload} disabled={compressing}>
                  <img
                    src="resume.png"
                    alt="Resume Icon"
                    className="button-icon"
                  />
                  Resume
                </button>
              ) : (
                <button onClick={handlePauseUpload} disabled={compressing}>
                  <img
                    src="pause.png"
                    alt="Pause Icon"
                    className="button-icon"
                  />
                  Pause
                </button>
              ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
